import { useTranslation } from 'next-i18next';
import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { ServerSharedText } from 'src/server-shared/texts/text';
import classes from './index.module.scss';
import { RoleAssignment, Roles, User, UserIdentification } from 'src/interfaces/user';
import CheckCirclePrimary24Px from 'public/svg-components/CheckCirclePrimary24Px';
import AddCircle24Px from 'public/svg-components/AddCircle24Px';
import { setCookies } from 'cookies-next';
import { CURRENT_ROLE, INVESTOR_ROLE_REQUEST } from 'src/constants/registration';
import { HOME_PAGE, PROFILE_COMPLETION_PAGE } from 'src/constants/paths';
import { useToggle } from 'src/hooks/useToggle';
import { SwitchRoleModal } from 'src/ui/menu/modals/SwitchRoleModal';
import { useUpdateUserMutation } from 'src/store/user/api';
import { getCurrentRole, setActiveNavigationItem, setCurrentRole } from 'src/store/app/reducer';
import { Status } from 'src/interfaces/common';
import { ServerSharedSimpleProgress } from 'src/server-shared/funding-stats/progress-bar';

interface SwitchRoleMenuProps {
  user: User | null;
  onHide: () => void;
  toggleIMContractModal: () => void;
  identificationDetails?: UserIdentification;
}

const roles = [
  {
    role: Roles.INVESTOR,
    title: 'aquaty-app.admin.filter.investor',
    addRole: 'Add Investor Role',
  },
  {
    role: Roles.INVESTMENT_MANAGER,
    title: 'aquaty-app.admin.filter.investment-manager',
    addRole: 'Add Investment Manager Role',
  },
];

export const SwitchRoleMenu = ({
  user,
  onHide,
  toggleIMContractModal,
  identificationDetails,
}: SwitchRoleMenuProps) => {
  const { t } = useTranslation();
  const { push } = useRouter();
  const dispatch = useDispatch();

  const [saveUser, { isLoading: saveUserLoading }] = useUpdateUserMutation();
  const role = useSelector(getCurrentRole);

  const [showSwitchRoleModal, toggleSwitchRoleModal] = useToggle();

  const switchRoleHandler = useCallback(
    async (hasRole: boolean, role: Roles, isActive: boolean, showRegistrationProgress: boolean) => {
      if (isActive && saveUserLoading) {
        return;
      }
      if (!isActive && showRegistrationProgress) {
        push(PROFILE_COMPLETION_PAGE);
        return;
      }
      if (!hasRole) {
        if (role === Roles.INVESTMENT_MANAGER && user) {
          toggleIMContractModal();
          return;
        }
        if (role === Roles.INVESTOR && user) {
          await saveUser({
            id: user.userId,
            value: {
              ...user,
              userRoles: [...user.userRoles, { role } as RoleAssignment],
            },
          });
          setCookies(INVESTOR_ROLE_REQUEST, true);
          push(PROFILE_COMPLETION_PAGE);
          return;
        }
      }
      setCookies(CURRENT_ROLE, role);
      dispatch(setCurrentRole({ role }));

      dispatch(setActiveNavigationItem(HOME_PAGE));
      push(HOME_PAGE);
    },
    [dispatch, push, saveUser, saveUserLoading, toggleIMContractModal, user],
  );

  const isCompletedInvestorRegistration = useMemo(() => {
    const investorRole = user?.userRoles.find(({ role }) => role === Roles.INVESTOR);
    return investorRole ? investorRole.verificationStatus === Status.APPROVED : true;
  }, [user?.userRoles]);

  const registrationProgress = useMemo(() => {
    if (identificationDetails) {
      return identificationDetails.verificationStatus === Status.APPROVED ? 2 : 1;
    }
    return 1;
  }, [identificationDetails]);

  if (!user) {
    return null;
  }

  return (
    <div className={classes['switch-role']} onPointerLeave={onHide}>
      <div className={classes['switch-role__menu']}>
        {roles.map((el) => {
          const hasRole = user.userRoles.some((roleAssignment) => roleAssignment.role === el.role);
          const isActive = role === el.role;

          const showRegistrationProgress =
            el.role === Roles.INVESTOR && !isCompletedInvestorRegistration;
          if (showRegistrationProgress) {
            return (
              <div
                key={el.role}
                onClick={() =>
                  switchRoleHandler(hasRole, el.role, isActive, showRegistrationProgress)
                }
                className={classes['switch-role__investor-registration']}
              >
                <div className="grid-row xxs">
                  <ServerSharedText color="body">
                    {t('aquaty-app.admin.filter.investor')}
                  </ServerSharedText>

                  <ServerSharedText color="bodyOpacity50">
                    {t(
                      'aquaty-generic.menu.roles.investor.you-need-complete-investor-role-registration',
                    )}
                  </ServerSharedText>
                </div>

                <div className="grid-column template-mc-1fr align-items-center">
                  <ServerSharedText color="bodyOpacity50">
                    {registrationProgress}/3
                  </ServerSharedText>

                  <ServerSharedSimpleProgress
                    current={registrationProgress}
                    expected={3}
                    color="warning600"
                    containerColor="bodyOpacity15"
                  />
                </div>

                <ServerSharedText color="primary600" type="label">
                  {t('aquaty-generic.cta.complete-profile')}
                </ServerSharedText>
              </div>
            );
          }

          return (
            <div
              key={el.role}
              onClick={() =>
                switchRoleHandler(hasRole, el.role, isActive, showRegistrationProgress)
              }
              className={classes['switch-role__item']}
            >
              <ServerSharedText color="body">{t(hasRole ? el.title : el.addRole)}</ServerSharedText>
              {isActive && <CheckCirclePrimary24Px />}
              {!hasRole && <AddCircle24Px className={classes['add-icon']} />}
            </div>
          );
        })}
      </div>

      {showSwitchRoleModal && <SwitchRoleModal show closeEvent={toggleSwitchRoleModal} />}
    </div>
  );
};
